import React from 'react';
import { RaceEngineerTip, EngineerState } from '../../models/responseBodies/AnalysisResponse';
import { currentTenant } from '../../tenant/tenants';

// Re-export types for backward compatibility
export type { RaceEngineerTip, EngineerState } from '../../models/responseBodies/AnalysisResponse';

interface RaceEngineerPanelProps {
  engineerState: EngineerState;
  activeTipId: number | null;
  onTipClick: (tip: RaceEngineerTip) => void;
  onAnalyze: () => void;
  isAnalyzeEnabled: boolean;
  isStale: boolean;
}

const SkeletonTips: React.FC = () => (
  <>
    {[1, 2, 3].map((i) => (
      <div key={i} className="re-skeleton">
        <div className="re-skeleton-num" />
        <div className="re-skeleton-lines">
          <div className="re-skeleton-line" />
          <div className="re-skeleton-line" />
        </div>
      </div>
    ))}
  </>
);

const RaceEngineerPanel: React.FC<RaceEngineerPanelProps> = ({
  engineerState,
  activeTipId,
  onTipClick,
  onAnalyze,
  isAnalyzeEnabled,
  isStale,
}) => {
  const { status, tips, error } = engineerState;

  const buttonText =
    status === 'loading'
      ? 'ANALYZING...'
      : status === 'error'
        ? 'RETRY'
        : (status === 'ready' || isStale)
          ? 'RE-ANALYZE'
          : 'ANALYZE';

  return (
    <div className="race-engineer">
      <div className="re-header">
        <span className="tele-toolbar-label" style={{ color: 'var(--tele-accent)' }}>
          {currentTenant.brand.shortName} AI Engineer
        </span>
        <button
          className="re-analyze-btn"
          onClick={onAnalyze}
          disabled={!isAnalyzeEnabled || status === 'loading'}
        >
          {buttonText}
        </button>
      </div>

      {status === 'idle' && (
        <div className="re-idle-msg">
          Select laps and click Analyze for AI coaching tips.
        </div>
      )}

      {status === 'loading' && (
        <div className="re-tips">
          <SkeletonTips />
        </div>
      )}

      {status === 'error' && (
        <div className="re-error-msg">
          {error || 'Analysis failed — try again.'}
        </div>
      )}

      {status === 'ready' && (
        <div className="re-tips">
          {(tips ?? []).map((tip, index) => (
            <div
              key={tip.id}
              className={`re-tip re-tip-enter ${activeTipId === tip.id ? 'active' : ''}`}
              style={{ animationDelay: `${index * 300}ms` }}
              onClick={() => onTipClick(tip)}
            >
              <div className="re-tip-num">{tip.id}</div>
              <div>
                <div className="re-tip-title">{tip.title}</div>
                <div
                  className="re-tip-desc re-tip-desc-reveal"
                  style={{ animationDelay: `${index * 300 + 200}ms` }}
                >
                  {tip.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RaceEngineerPanel;
